/**
 * EL ADAPTADOR DEL VEHÍCULO.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  ESTE ES EL ÚNICO ARCHIVO QUE CONOCE LA FORMA DEL API DE VEHÍCULOS.       ║
 * ║  El contrato vive en `api/src/routes/vehicles.ts` (+ services/vehicles.ts).║
 * ║  `Vehicles.js`, `VehicleDetail.js` y `VehicleDetailDesktop.js` siguen      ║
 * ║  hablando de `currentMileage`, `photoURLs`, `cost` y `visibility`, que son ║
 * ║  los nombres que la app ya usa. Si el contrato cambia, se ajusta AQUÍ.     ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * Mismo patrón —y el mismo motivo— que `catalog.js`: esta semana hubo que
 * unificar cuatro copias del mismo lector de Firestore repartidas por las
 * pantallas. Un quinto `fetch` escrito dentro de `VehicleDetail.js` sería
 * exactamente la deuda que esta migración está pagando.
 *
 * ─── LAS SIETE TRAICIONES DE ESTE DOMINIO, POR LO QUE CUESTAN ───────────────
 *
 * Ninguna da un error. Todas dan un dato distinto, y en este dominio el dato es
 * lo que se le enseña a quien va a comprar el carro.
 *
 *  1. 🔴 LAS FECHAS. Media app hace `record.serviceDate.toDate()`. Un texto ISO
 *     no tiene `.toDate()`, así que `getVehicleAlerts` compararía
 *     `undefined > undefined` → `false` → GANA EL REGISTRO MÁS VIEJO y todas las
 *     alertas del carro se calculan sobre él. Por eso las fechas salen de aquí
 *     como SELLO DE TIEMPO (`{ toDate, toMillis }`), no como texto.
 *  2. EL CARRO DE ÉPOCA. El API dice `isVintage:true` + `year:null`; la app
 *     compara `vehicle.year === 'vintage'`.
 *  3. LOS ENUMS. Postgres habla en MAYÚSCULAS ('CERTIFIED', 'AUTO', 'MECHANIC')
 *     y la app compara en minúsculas. `certStatus()` devolvería 'CERTIFIED', que
 *     no es 'certified', y un Servicio Certificado saldría como PENDIENTE.
 *  4. EL DINERO. `costCents` no es `cost`. Y un coste ausente no es cero: cero
 *     afirma "fue gratis".
 *  5. LOS DOCUMENTOS. El API los da como LISTA `[{kind, expiresOn}]` y la app
 *     los lee como OBJETO `documents.marbete.expiresAt`. Sin puente, los avisos
 *     de marbete y seguro se apagan sin decir nada.
 *  6. LOS DIAGNÓSTICOS VIENEN RECORTADOS A PROPÓSITO (metadatos + 160
 *     caracteres). Ver el recuadro de más abajo.
 *  7. EL 404 NO ES UN ERROR. Ver el recuadro del final.
 */
import { apiFetch, ApiError, GET_CACHE_MS } from './client';

/**
 * 50 por página en todo el dominio. No es una preferencia: es `MAX_PAGE_SIZE`
 * en `api/src/services/vehicles.ts`, y pedir más devuelve 400.
 */
export const VEHICLES_PAGE_SIZE = 50;
export const RECORDS_PAGE_SIZE = 50;
export const FUEL_PAGE_SIZE = 50;
export const DIAGNOSES_PAGE_SIZE = 50;
export const MESSAGES_PAGE_SIZE = 50;

/**
 * Tope de profundidad del API (`MAX_PAGE`). Pedir la 201 no es "sale vacía": es
 * un 400, o sea la pantalla en rojo por culpa nuestra. Se recorta aquí.
 */
export const VEHICLES_MAX_PAGE = 200;

// ────────────────────────────────────────────────────────────────────────────
// PIEZAS SUELTAS
// ────────────────────────────────────────────────────────────────────────────

/**
 * 🔴 EL SELLO DE TIEMPO. Ver la traición nº 1 del encabezado.
 *
 * Se devuelve un objeto con la MISMA superficie que un `Timestamp` de Firestore
 * (`toDate()` / `toMillis()`) en vez de un `Date` pelado, porque la app no llama
 * a `new Date(x)`: llama a `x.toDate()`. Convertir en el borde es exactamente
 * para lo que existe un adaptador; el JSX no tiene que enterarse de qué base
 * vino el dato.
 *
 * Una fecha ausente o ilegible es `null`, nunca la época de Unix: un 1 de enero
 * de 1970 en el historial es un dato falso, y encima uno que se pinta.
 */
export function ts(iso) {
  if (iso === null || iso === undefined || iso === '') return null;
  if (iso && typeof iso.toDate === 'function') return iso;      // ya venía sellado
  const ms = iso instanceof Date ? iso.getTime() : Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return { toDate: () => new Date(ms), toMillis: () => ms };
}

/** Fecha suelta (no sellada). Para lo que la pantalla usa como `Date`. */
function fecha(iso) {
  const s = ts(iso);
  return s ? s.toDate() : null;
}

/** Número o `null`. `0` es un número; `''` y `undefined` no lo son. */
function nul(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Centavos → pesos. `null` se conserva.
 *
 * 🔴 `Number(x) || 0` es el bug documentado en el contrato del catálogo: convertía
 * la ausencia en un cero, y un cero AFIRMA algo ("este servicio fue gratis").
 */
function pesos(cents) {
  const n = nul(cents);
  return n === null ? null : n / 100;
}

/** 'CERTIFIED' → 'certified'. Ver la traición nº 3. */
function minus(v) {
  return typeof v === 'string' && v ? v.toLowerCase() : null;
}

/**
 * 🔴 QUIÉN HIZO EL TRABAJO: el guion y el guion bajo NO son el mismo enum.
 *
 * La app escribe `'ai-assisted'` (`Diagnose.js`) y `'voice-assistant'`
 * (`assistant/live.js`) CON GUION; la columna de Postgres es el enum
 * `PerformedBy` con los valores `SELF | AI_ASSISTED | MECHANIC |
 * VOICE_ASSISTANT`, CON GUION BAJO. Un `toUpperCase()` a secas produce
 * `'AI-ASSISTED'`, que no está en el enum, y como el esquema del API es
 * `.strict()` eso NO es un campo que se ignora: es un 400 que tumba el guardado
 * ENTERO. O sea que guardar un servicio desde un diagnóstico —o por voz— dejaría
 * de funcionar el día que se encienda el dominio, y el usuario solo vería
 * "no se pudo guardar".
 *
 * Se traduce en el borde, en los dos sentidos, para que ni la app tenga que
 * aprender el vocabulario de la base ni la base el de la app.
 */
function performedByApi(v) {
  const s = typeof v === 'string' ? v.trim() : '';
  return s ? s.replace(/-/g, '_').toUpperCase() : null;
}

/** 'AI_ASSISTED' → 'ai-assisted'. El inverso exacto de `performedByApi`. */
function performedByApp(v) {
  const s = minus(v);
  return s ? s.replace(/_/g, '-') : null;
}

function lista(v) {
  return Array.isArray(v) ? v : [];
}

/**
 * El armazón de página, común a las cinco listas.
 *
 * `total` es el del conjunto FILTRADO en el servidor, no el de la página: es lo
 * que permite decir "1 de 3" en vez de un "cargar más" que nunca dice cuánto
 * falta. Si el API no lo mandara, `totalPages` sale `null` y la pantalla lo dice
 * como pueda — inventarse un "de 7" sería la misma clase de mentira que estamos
 * quitando.
 */
function adaptPage(payload, campo, mapear, { page, pageSize, que }) {
  const items = payload && (payload.items ?? payload[campo]);
  if (!Array.isArray(items)) {
    throw new ApiError(`El API no devolvió una lista de ${que}.`, { code: 'bad_response' });
  }
  const crudo = payload.total ?? payload.totalCount ?? payload.count;
  const total = crudo === null || crudo === undefined || !Number.isFinite(Number(crudo))
    ? null
    : Number(crudo);

  const tam = Number(payload.pageSize) || pageSize;
  const delServidor = Number(payload.totalPages);

  return {
    [campo]: items.map(mapear).filter(Boolean),
    total,
    // Sin resultados sigue habiendo UNA página: no existe "página 1 de 0".
    totalPages: Number.isFinite(delServidor) && delServidor > 0
      ? delServidor
      : (total === null ? null : Math.max(1, Math.ceil(total / tam))),
    page: Number(payload.page) || page,
    pageSize: tam,
  };
}

/**
 * Página y tamaño, recortados.
 *
 * ⚠️ Los esquemas del API son `.strict()`: un parámetro que no conozca no se
 * ignora, devuelve 400. Por eso esto es una lista de PERMITIDOS construida campo
 * a campo y jamás un `...filtros`: un `...` es una lista de prohibidos
 * disfrazada, y falla al AÑADIR un campo.
 */
export function pageQuery({ page = 1, pageSize } = {}, porDefecto) {
  const n = Math.floor(Number(page));
  const p = Number.isFinite(n) ? Math.min(VEHICLES_MAX_PAGE, Math.max(1, n)) : 1;
  const tam = Number(pageSize);
  return { page: p, pageSize: Number.isFinite(tam) && tam > 0 ? Math.min(porDefecto, tam) : porDefecto };
}

/**
 * La consulta de la LISTA de vehículos.
 *
 * `userId` solo lo acepta el API a un admin (el panel `/admin/user/:id`); a
 * cualquier otro le responde 403 en vez de una lista vacía, que diría "no tiene
 * vehículos" cuando lo que pasa es que no es su cuenta.
 */
export function vehiclePageQuery(filtros = {}) {
  const q = pageQuery(filtros, VEHICLES_PAGE_SIZE);
  const uid = typeof filtros.userId === 'string' ? filtros.userId.trim() : '';
  if (uid) q.userId = uid;
  return q;
}

// ────────────────────────────────────────────────────────────────────────────
// EL VEHÍCULO
// ────────────────────────────────────────────────────────────────────────────

/**
 * El acceso de quien está mirando.
 *
 * 🔴 SIN BLOQUE DE ACCESO NO SE ASUME PROPIEDAD. La ficha de la LISTA no lo trae
 * (son todos tuyos por definición), y leer la ausencia como "eres el dueño"
 * pondría botones de dueño en cualquier pantalla que reutilice una tarjeta.
 * `null` significa "no se sabe", y quien no sabe no manda.
 */
function adaptAccess(a) {
  if (!a || typeof a !== 'object') return null;
  const level = typeof a.level === 'string' ? a.level : null;
  return {
    level,
    ownerId: a.ownerId ?? null,
    canEdit: a.canEdit === true,
    // `owner` y `admin` no caducan; el resto sí, y la pantalla lo enseña.
    expires: fecha(a.expiresAt),
    expiresAt: ts(a.expiresAt),
    isOwner: level === 'owner',
    isAdmin: level === 'admin',
    isShared: level === 'shared' || level === 'workshop',
  };
}

/**
 * Los documentos, de LISTA a OBJETO. Ver la traición nº 5.
 *
 * Solo se publican las clases que el API devolvió. Rellenar las cuatro con
 * `null` haría que `VehicleDocuments` creyera que el dueño decidió "sin fecha"
 * en las que ni siquiera se preguntaron.
 */
function adaptDocuments(raw) {
  const out = {};
  for (const d of lista(raw)) {
    if (!d || typeof d !== 'object') continue;
    const clave = minus(d.kind);
    if (!clave) continue;
    out[clave] = { expiresAt: d.expiresOn ?? null };
  }
  return out;
}

/**
 * Una fila de vehículo (tarjeta o ficha) con los nombres de la app.
 *
 * `photoURL` se rellena con la primera foto porque medio proyecto la lee (el
 * héroe, el PDF, las tarjetas del garaje) y `photoURLs` es lo que lee el resto:
 * publicar las dos aquí evita que cada pantalla tenga que saber de dónde vino
 * el carro. Sin fotos, `photoURL` es `null` y NO `''`, que se pinta como una
 * imagen rota.
 */
export function adaptVehicle(row) {
  if (!row || typeof row !== 'object') return null;
  const fotos = lista(row.photos).filter(Boolean);
  const anio = nul(row.year);

  return {
    id: row.id,
    userId: row.userId ?? null,
    brand: row.brand ?? '',
    model: row.model ?? '',
    // 🔴 El sentinela del carro de época. Ver la traición nº 2.
    year: row.isVintage === true ? 'vintage' : anio,
    isVintage: row.isVintage === true,
    currentMileage: nul(row.currentMileageKm),
    plate: row.plate ?? null,
    // El enum de la base va en MAYÚSCULAS; toda la app compara en minúsculas.
    vehicleType: minus(row.vehicleType) || 'auto',
    isCommercial: row.isCommercial === true,
    photoURLs: fotos,
    photoURL: fotos[0] ?? null,
    lastMileageUpdate: ts(row.lastMileageUpdate),
    createdAt: ts(row.createdAt),

    // ─── Solo en la FICHA ────────────────────────────────────────────────
    // ⚠️ AUSENTE NO ES NULL en los intervalos: la clave que falta hereda el
    // valor por defecto y la clave en `null` es una decisión explícita ("este
    // servicio no se mide por ese eje"). Se transporta tal cual; rellenarlo
    // congelaría cada carro con los números de hoy.
    specs: row.specs ?? null,
    maintenanceIntervals: row.maintenanceIntervals ?? null,
    documents: adaptDocuments(row.documents),
    updatedAt: ts(row.updatedAt),
    access: adaptAccess(row.access),
  };
}

export function adaptVehiclePage(payload, opts = {}) {
  return adaptPage(payload, 'vehicles', adaptVehicle, {
    page: opts.page || 1, pageSize: opts.pageSize || VEHICLES_PAGE_SIZE, que: 'vehículos',
  });
}

// ────────────────────────────────────────────────────────────────────────────
// EL HISTORIAL DE MANTENIMIENTO
// ────────────────────────────────────────────────────────────────────────────

/**
 * Un servicio del historial.
 *
 * ⚠️ `customNextKm` es un INTERVALO que se suma al kilometraje del servicio, no
 * un odómetro absoluto. El nombre engaña y ya causó un bug real; se conserva tal
 * cual porque es el nombre que `calculateNextMaintenance` espera.
 */
export function adaptRecord(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    id: row.id,
    vehicleId: row.vehicleId ?? null,
    serviceTypeCode: row.serviceTypeCode ?? null,
    serviceType: row.serviceType ?? '',
    description: row.description ?? null,
    mileageAtService: nul(row.mileageAtServiceKm),
    distanceUnit: minus(row.inputUnit),
    cost: pesos(row.costCents),
    serviceDate: ts(row.serviceDate),
    alertDate: ts(row.alertDate),
    customNextKm: nul(row.customNextKm),
    notes: row.notes ?? null,
    hasWarranty: row.hasWarranty === true,
    warrantyExpiresAt: ts(row.warrantyExpiresAt),
    invoicePhotoURL: row.invoicePhotoUrl ?? null,
    invoicePendingUpload: row.invoicePendingUpload === true,
    performedBy: performedByApp(row.performedBy),
    mechanicId: row.mechanicId ?? null,
    appointmentId: row.appointmentId ?? null,
    clientConfirmed: row.clientConfirmed === true,
    confirmedAt: ts(row.confirmedAt),
    // La pantalla filtra por `visibility !== 'private'`; el API lo dice como
    // booleano. Se traduce el nombre, no el significado.
    visibility: row.isPrivate === true ? 'private' : 'public',
    // 🔴 El sello del doble testigo, en minúsculas: `certStatus()` compara con
    // 'certified' y un 'CERTIFIED' saldría como PENDIENTE.
    certificationStatus: minus(row.certificationStatus),
    certifiedBy: row.certifiedBy ?? null,
    createdAt: ts(row.createdAt),
  };
}

export function adaptRecordPage(payload, opts = {}) {
  return adaptPage(payload, 'records', adaptRecord, {
    page: opts.page || 1, pageSize: opts.pageSize || RECORDS_PAGE_SIZE, que: 'servicios',
  });
}

// ────────────────────────────────────────────────────────────────────────────
// COMBUSTIBLE
// ────────────────────────────────────────────────────────────────────────────

export function adaptFuelLog(row) {
  if (!row || typeof row !== 'object') return null;
  const km = nul(row.kmDriven);
  const gal = nul(row.gallons);
  const eff = nul(row.efficiency);
  return {
    id: row.id,
    vehicleId: row.vehicleId ?? null,
    kmDriven: km,
    gallons: gal,
    // El mismo respaldo que ya vive en el código (`log.efficiency || km/gal`):
    // la columna derivada admite nulos en filas antiguas.
    efficiency: eff !== null ? eff : (km !== null && gal ? km / gal : null),
    condition: minus(row.condition) || 'mixed',
    createdAt: ts(row.createdAt),
  };
}

/**
 * La página de tanqueos, con su RESUMEN.
 *
 * 🔴 EL RESUMEN ES LO QUE HACE QUE PAGINAR NO MIENTA. Sin él, "tu consumo" se
 * convertiría en "el consumo de los últimos 50 tanqueos", que es otro dato. Y si
 * el servidor no lo manda, NO se calcula con la página: se dice que no hay.
 */
export function adaptFuelPage(payload, opts = {}) {
  const base = adaptPage(payload, 'logs', adaptFuelLog, {
    page: opts.page || 1, pageSize: opts.pageSize || FUEL_PAGE_SIZE, que: 'tanqueos',
  });
  const s = (payload && payload.summary) || {};
  return {
    ...base,
    summary: {
      count: nul(s.count) ?? 0,
      recentKmPerGallon: nul(s.recentKmPerGallon),
      allTimeKmPerGallon: nul(s.allTimeKmPerGallon),
    },
  };
}

// ────────────────────────────────────────────────────────────────────────────
// DIAGNÓSTICOS DE IA
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ 🔴 EL LISTADO NO TRAE NI UNA CONVERSACIÓN, Y ESO ES EL DISEÑO            ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// Un carro con 20 diagnósticos de 40 mensajes son ~1,6 MB en UNA respuesta, y la
// pantalla los pedía TODOS para pintar tres títulos. El API devuelve metadatos y
// un anticipo de 160 caracteres; la conversación va por `/diagnoses/:id/messages`,
// paginada, y solo cuando alguien abre una.
//
// `messages` se publica como `null` A PROPÓSITO —y no como el anticipo partido en
// trozos— porque un `messages` con contenido es indistinguible de la conversación
// completa: el modal enseñaría 160 caracteres como si fuera todo lo que se habló,
// y la pantalla no tendría forma de saber que le falta el resto. `null` obliga a
// pedirla, que es justo lo que se quiere.
// ────────────────────────────────────────────────────────────────────────────

export function adaptDiagnosis(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    id: row.id,
    vehicleId: row.vehicleId ?? null,
    mode: row.mode ?? null,
    messageCount: nul(row.messageCount) ?? 0,
    preview: row.preview ?? null,
    mergedFromCount: nul(row.mergedFromCount) ?? 1,
    createdAt: ts(row.createdAt),
    messages: null,        // ver el recuadro
  };
}

export function adaptDiagnosisPage(payload, opts = {}) {
  return adaptPage(payload, 'diagnoses', adaptDiagnosis, {
    page: opts.page || 1, pageSize: opts.pageSize || DIAGNOSES_PAGE_SIZE, que: 'diagnósticos',
  });
}

/**
 * Un mensaje de la conversación.
 *
 * El rol de la base es 'USER' | 'MODEL'; la app pinta la burbuja comparando
 * `msg.role === 'user'`. 'MODEL' se traduce a 'assistant' porque es el nombre
 * que ya usan el chat y el asistente — cualquier cosa que no sea 'user' cae del
 * lado de la máquina, que es lo que la pantalla necesita saber.
 */
export function adaptMessage(row) {
  if (!row || typeof row !== 'object') return null;
  const rol = minus(row.role);
  return {
    seq: nul(row.seq) ?? 0,
    role: rol === 'user' ? 'user' : 'assistant',
    content: typeof row.content === 'string' ? row.content : '',
    createdAt: ts(row.createdAt),
  };
}

export function adaptMessagePage(payload, opts = {}) {
  return adaptPage(payload, 'messages', adaptMessage, {
    page: opts.page || 1, pageSize: opts.pageSize || MESSAGES_PAGE_SIZE, que: 'mensajes',
  });
}

// ════════════════════════════════════════════════════════════════════════════
// LAS LLAMADAS
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ ⚠️ EL 404 NO ES UN ERROR, ES UNA RESPUESTA — y aquí significa DOS cosas  ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
//   · En `/vehicles/:id` → «no existe, o no es tuyo». Los dos casos comparten
//     código a propósito: un 403 confirmaría que ese identificador existe y con
//     eso se enumera la flota de la base.
//   · En `/vehicles/:id/fuel-logs` y `/diagnoses` → «ese dato no te corresponde».
//     Los tanqueos solo los ve el dueño y el admin; los diagnósticos, el dueño,
//     el admin y el acceso compartido —nunca el taller—. Es la regla
//     funcionando, no un fallo.
//
// Las dos se devuelven como `null`, y la pantalla decide qué decir. Pintarlo de
// rojo con «no se pudo cargar» le mentiría al usuario y le haría reintentar para
// siempre algo que funciona exactamente como se diseñó.
//
// 🔴 CUALQUIER OTRO FALLO SUBE. Una red caída, un 403 o un 500 tienen que llegar
// arriba con su tipo y su código: un historial vacío por un problema de red no
// puede parecer un carro sin historial, y en esta app eso importa el doble
// porque el historial es lo que se le enseña a quien compra el carro.
// ════════════════════════════════════════════════════════════════════════════

/** ¿Es el 404 que significa «no hay nada que enseñarte aquí»? */
function esNoEncontrado(err) {
  return !!err && (err.status === 404 || err.code === 'not_found');
}

// ════════════════════════════════════════════════════════════════════════════
// EL MISMO GARAJE, PEDIDO UNA SOLA VEZ
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ 🔴 DOS PANTALLAS PIDEN LO MISMO AL ARRANCAR, Y NO SE ENTERAN LA UNA DE   ║
// ║    LA OTRA: el home (`Home.js`) y el MENÚ INFERIOR, que está montado en  ║
// ║    todas las pantallas y solo quiere un número para el ícono de salud.   ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// Medido contra producción (móvil, 4G lenta): `GET /vehicles` y los tres
// `GET /vehicles/:id/records` salían DOS VECES en 3 de las 4 condiciones
// medidas — ocho peticiones de más mientras el cliente mira una pantalla vacía.
//
// ─── ¿NO LO CUBRE YA EL CACHÉ DE `client.js`? NO, Y ESTÁ MEDIDO ─────────────
//
// `src/api/client.js` une los GET que coinciden EN VUELO y además guarda la
// última respuesta `GET_CACHE_MS`. Pero su caché **sirve y revalida**: en un
// acierto devuelve lo guardado al instante Y dispara un refresco por detrás
// (`revalidarPorDetras`), que es un viaje de red de verdad. Para quien MIRA la
// pantalla eso es perfecto —no espera—, pero el fallo que se fue a cerrar aquí
// no era la espera del menú: eran OCHO PETICIONES compitiendo por el ancho de
// banda en 4G para pintar un número de 24 px.
//
// Comprobado bajando la prueba al nivel de la red (`BottomNav.saludCompartida`,
// doblando `fetch` y no `apiFetch`): sin esta memoria, con el home ya servido,
// el menú vuelve a gastar `/vehicles` + `/vehicles/:id/records`. Con ella, cero.
// La diferencia no es de latencia, es de viajes.
//
// Por eso esto NO es «el mismo caché otra vez»: el de abajo evita la ESPERA, y
// este evita la PREGUNTA. Y por eso vive aquí y no en cada pantalla: este
// archivo ya es el único que sabe pedir la flota (ver el recuadro de
// `fetchFleetRecords`), así que es el único sitio donde una segunda pantalla
// reaprovecha lo que pidió la primera sin que ninguna conozca a la otra.
//
// ⚠️ LO QUE SE ACEPTA A CAMBIO, dicho claro: durante `FLOTA_TTL_MS` una segunda
// lectura puede devolver la foto anterior. Se acota por tres vías:
//   · el plazo NO se inventa aquí: es el MISMO `GET_CACHE_MS` de `client.js`,
//     importado y no copiado, para que no haya dos plazos que alguien tenga que
//     mantener a la par (y para que esta capa no pueda sobrevivir a la de
//     abajo, que sería justo el dato viejo sin dueño);
//   · CUALQUIER escritura del dominio la vacía (`trasEscribir`), así que un
//     carro recién agregado, un kilometraje movido o un servicio registrado
//     desde esta misma app se ven de inmediato. Lo que puede llegar tarde es una
//     escritura hecha desde OTRO aparato o por el taller — exactamente la misma
//     ventana que ya acepta `client.js`, ni un segundo más;
//   · un `null` o un fallo NO se memorizan: solo se comparte una respuesta buena.
//
// ⚠️ Quien inyecta su propio transporte (`apiFetchImpl`, las pruebas) o pasa un
// `signal` NO pasa por aquí: la clave no distingue transportes, y compartir la
// respuesta de un doble con la del API de verdad sería un dato de otra base.
// ════════════════════════════════════════════════════════════════════════════

const FLOTA_TTL_MS = GET_CACHE_MS;
const memoriaFlota = new Map();

/** Vacía la memoria de la flota. La usan las escrituras (y las pruebas). */
export function limpiarMemoriaFlota() {
  memoriaFlota.clear();
}

function conMemoria(clave, pedir) {
  const guardado = memoriaFlota.get(clave);
  if (guardado) {
    if (guardado.promesa) return guardado.promesa;          // ya va en camino
    if (Date.now() - guardado.t < FLOTA_TTL_MS) return Promise.resolve(guardado.valor);
    memoriaFlota.delete(clave);                             // caducada
  }
  const promesa = pedir();
  memoriaFlota.set(clave, { promesa });
  promesa.then(
    (valor) => {
      // Si una escritura vació la memoria mientras esto viajaba, NO se repone:
      // esa respuesta es anterior a la escritura.
      const actual = memoriaFlota.get(clave);
      if (actual && actual.promesa === promesa) memoriaFlota.set(clave, { t: Date.now(), valor });
    },
    () => {
      const actual = memoriaFlota.get(clave);
      if (actual && actual.promesa === promesa) memoriaFlota.delete(clave);
    },
  );
  return promesa;
}

/**
 * Toda escritura del dominio vacía la memoria: al salir y al volver.
 *
 * Al SALIR, para que una lectura posterior no se sirva de la foto anterior; al
 * VOLVER, porque una lectura que viajaba a la vez que la escritura trae datos
 * de antes y no puede quedarse guardada como si fueran los de ahora.
 */
function trasEscribir(promesa) {
  limpiarMemoriaFlota();
  return promesa.then(
    (v) => { limpiarMemoriaFlota(); return v; },
    (e) => { limpiarMemoriaFlota(); throw e; },
  );
}

/** Los vehículos de quien llama (o los de otra cuenta, si es admin). */
export async function fetchVehicles(filtros = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const query = vehiclePageQuery(filtros);
  const pedir = async () => adaptVehiclePage(await call('/vehicles', { query, signal }), query);
  if (apiFetchImpl || signal) return pedir();
  return conMemoria(`vehicles|${JSON.stringify(query)}`, pedir);
}

/** La ficha. `null` = no existe o no se puede ver. */
export async function fetchVehicle(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  try {
    return adaptVehicle(await call(`/vehicles/${encodeURIComponent(id)}`, { signal }));
  } catch (err) {
    if (esNoEncontrado(err)) return null;
    throw err;
  }
}

/** El historial. `null` = no te corresponde (el alcance `records` te excluye). */
export async function fetchRecords(id, filtros = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const query = pageQuery(filtros, RECORDS_PAGE_SIZE);
  try {
    const payload = await call(`/vehicles/${encodeURIComponent(id)}/records`, { query, signal });
    return adaptRecordPage(payload, query);
  } catch (err) {
    if (esNoEncontrado(err)) return null;
    throw err;
  }
}

/** Los tanqueos + el resumen. `null` = solo el dueño y el admin los ven. */
export async function fetchFuelLogs(id, filtros = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const query = pageQuery(filtros, FUEL_PAGE_SIZE);
  try {
    const payload = await call(`/vehicles/${encodeURIComponent(id)}/fuel-logs`, { query, signal });
    return adaptFuelPage(payload, query);
  } catch (err) {
    if (esNoEncontrado(err)) return null;
    throw err;
  }
}

/** Metadatos + anticipo. `null` = el taller no ve lo que el dueño le contó a una IA. */
export async function fetchDiagnoses(id, filtros = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const query = pageQuery(filtros, DIAGNOSES_PAGE_SIZE);
  try {
    const payload = await call(`/vehicles/${encodeURIComponent(id)}/diagnoses`, { query, signal });
    return adaptDiagnosisPage(payload, query);
  } catch (err) {
    if (esNoEncontrado(err)) return null;
    throw err;
  }
}

/**
 * La conversación de UN diagnóstico. Cuelga del diagnóstico, pero la
 * autorización arranca del vehículo (lo resuelve el API).
 */
export async function fetchDiagnosisMessages(diagnosisId, filtros = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const query = pageQuery(filtros, MESSAGES_PAGE_SIZE);
  try {
    const payload = await call(`/diagnoses/${encodeURIComponent(diagnosisId)}/messages`, { query, signal });
    return adaptMessagePage(payload, query);
  } catch (err) {
    if (esNoEncontrado(err)) return null;
    throw err;
  }
}

// ════════════════════════════════════════════════════════════════════════════
// LAS ESCRITURAS
//
// El API las tenía desde el principio (ocho rutas) y este archivo no exponía
// NINGUNA: solo lectores. Ese desnivel es lo que hacía ILEGAL encender el
// dominio, y no por una regla de estilo — `migrationConfig.js` lo dice con
// todas las letras: leer de Postgres mientras se escribe en Firestore deja al
// cliente agregando su carro y no volviéndolo a ver. Sin esta capa, la lectura
// migrada no servía para nada.
//
// 🔴 NINGUNA SE TRAGA UN FALLO. Los lectores de arriba convierten el 404 en
// `null` porque «no hay historial» es una respuesta legítima que la pantalla
// sabe pintar. Al escribir NO hay equivalente: un guardado que falla y devuelve
// null se convierte en una pantalla que dice «guardado» sobre algo que no se
// guardó, que es el fallo con más antecedentes en este proyecto. Todo sube con
// su tipo y su código para que la página pueda contar el motivo real.
// ════════════════════════════════════════════════════════════════════════════

/**
 * ⚠️ LOS CUERPOS SE CONSTRUYEN AQUÍ, CAMPO A CAMPO, Y NUNCA CON `...datos`.
 *
 * Los esquemas del API son `.strict()`: un campo que no conozca NO se ignora,
 * responde 400 nombrándolo. Verificado contra producción — `POST /vehicles` con
 * `currentMileage` (el nombre de la app) contesta:
 *
 *   «campo no permitido: currentMileage. El duenno de un vehiculo no cambia
 *    (userId), el odometro se mueve por POST /vehicles/:id/mileage, y plateNorm
 *    la calcula la base.»
 *
 * O sea que un `...vehicleData` del formulario de hoy —que lleva `plateNorm`,
 * `photoURL`, `updatedAt`, `userId`— no guarda a medias: no guarda NADA. Por eso
 * esto es una lista de PERMITIDOS construida a mano, igual que `productApiBody`
 * del catálogo: una lista de prohibidos falla al AÑADIR un campo.
 */
function iso(v) {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

/** La ficha del carro, con los nombres del API. */
export function vehicleApiBody(data = {}, { create = false } = {}) {
  const anio = data.year === 'vintage' ? null : nul(data.year);
  const placa = typeof data.plate === 'string' ? data.plate.trim() : '';
  const body = {
    brand: data.brand ?? '',
    model: data.model ?? '',
    // El sentinela 'vintage' de la app se parte en dos campos del API.
    year: anio,
    isVintage: data.year === 'vintage',
    // `''` no es «sin placa» para un esquema de texto: se manda `null`, que es
    // lo que la columna entiende como ausente.
    plate: placa ? placa.toUpperCase() : null,
    vehicleType: String(data.vehicleType || 'auto').toUpperCase(),
    isCommercial: data.isCommercial === true,
    photoUrls: lista(data.photoURLs).filter(Boolean),
  };
  // `undefined` = «no toques esto»; `null` = «vuelve al valor por defecto».
  if (data.maintenanceIntervals !== undefined) {
    body.maintenanceIntervals = data.maintenanceIntervals ?? null;
  }
  if (data.specs !== undefined) body.specs = data.specs ?? null;
  // 🔴 El odómetro SOLO se admite al nacer. En la edición va por su ruta.
  if (create && data.currentMileage !== undefined && data.currentMileage !== null) {
    body.currentMileageKm = nul(data.currentMileage);
  }
  return body;
}

/** Un servicio del historial, con los nombres del API. */
export function recordApiBody(data = {}) {
  const body = {
    serviceType: data.serviceType ?? '',
    serviceDate: iso(data.serviceDate) || new Date().toISOString(),
    description: data.description ?? null,
    mileageAtServiceKm: nul(data.mileageAtService),
    inputUnit: data.distanceUnit ? String(data.distanceUnit).toUpperCase() : null,
    // 🔴 Pesos → centavos. Un coste ausente NO es cero: cero afirma «fue gratis».
    costCents: data.cost === null || data.cost === undefined || data.cost === ''
      ? null
      : Math.round(Number(data.cost) * 100),
    alertDate: iso(data.alertDate),
    customNextKm: nul(data.customNextKm),
    notes: data.notes ?? null,
    hasWarranty: data.hasWarranty === true,
    warrantyExpiresAt: iso(data.warrantyExpiresAt),
    invoicePhotoUrl: data.invoicePhotoURL ?? null,
    invoicePendingUpload: data.invoicePendingUpload === true,
    performedBy: performedByApi(data.performedBy),
  };
  if (data.mechanicId !== undefined) body.mechanicId = data.mechanicId ?? null;
  if (data.appointmentId !== undefined) body.appointmentId = data.appointmentId ?? null;
  if (data.visibility !== undefined) body.isPrivate = data.visibility === 'private';
  return body;
}

/** Alta del carro. Devuelve el acuse con el id que asignó el servidor. */
export async function createVehicle(body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return trasEscribir(call('/vehicles', { method: 'POST', body, signal }));
}

/** La ficha (marca, modelo, placa, foto). El kilometraje va por su propia ruta. */
export async function updateVehicle(id, body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return trasEscribir(call(`/vehicles/${encodeURIComponent(id)}`, { method: 'PATCH', body, signal }));
}

/** Baja del carro. Solo el dueño o un administrador. */
export async function deleteVehicle(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return trasEscribir(call(`/vehicles/${encodeURIComponent(id)}`, { method: 'DELETE', signal }));
}

/**
 * El kilometraje, por RUTA APARTE y no dentro de la ficha.
 *
 * Esa separación no es cosmética: el kilometraje es de donde salen las alertas
 * de mantenimiento, así que solo debe moverse hacia adelante y con un motivo.
 * Mezclarlo en el PATCH general lo convertiría en un campo más que cualquier
 * guardado de la ficha puede pisar sin querer.
 *
 * 🔴 Es POST, no PATCH. La ruta es `POST /vehicles/:id/mileage`
 * (`api/src/routes/vehicles.ts`), y el verbo equivocado NO da un 405 que se note:
 * da un **404**, indistinguible de «ese carro no existe». Comprobado contra el
 * API en producción: PATCH → 404, POST → 200 con el acuse del odómetro.
 */
export async function setVehicleMileage(id, body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return trasEscribir(call(`/vehicles/${encodeURIComponent(id)}/mileage`, { method: 'POST', body, signal }));
}

/** Un servicio en el historial. */
export async function createRecord(vehicleId, body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return trasEscribir(call(`/vehicles/${encodeURIComponent(vehicleId)}/records`, { method: 'POST', body, signal }));
}

export async function updateRecord(vehicleId, recordId, body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return trasEscribir(call(
    `/vehicles/${encodeURIComponent(vehicleId)}/records/${encodeURIComponent(recordId)}`,
    { method: 'PATCH', body, signal },
  ));
}

/**
 * El cliente da por bueno el trabajo del taller. Ruta aparte por lo mismo.
 *
 * 🔴 VA UN CUERPO VACÍO A PROPÓSITO, y no es adorno. El servidor no lo mira
 * (`confirmRecord` no parsea nada), pero un POST sin cuerpo sale sin
 * `Content-Length` y la puerta de Google lo rechaza ANTES de llegar al API:
 * **411 Length Required**, con una página HTML que ni siquiera es JSON.
 * Comprobado contra producción: sin cuerpo → 411; con `{}` → 200. Mandar `{}`
 * quita la duda en cualquier cliente en vez de depender de que el navegador
 * añada el encabezado por su cuenta.
 */
export async function confirmRecord(vehicleId, recordId, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return trasEscribir(call(
    `/vehicles/${encodeURIComponent(vehicleId)}/records/${encodeURIComponent(recordId)}/confirm`,
    { method: 'POST', body: {}, signal },
  ));
}

/** Un tanqueo. El rendimiento lo calcula el servidor. */
export async function createFuelLog(vehicleId, body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/vehicles/${encodeURIComponent(vehicleId)}/fuel-logs`, { method: 'POST', body, signal });
}

// ────────────────────────────────────────────────────────────────────────────
// LOS DOCUMENTOS DEL CARRO — uno por llamada, y esa es la corrección
//
// 🔴 NO HAY `saveDocuments(vehicleId, todos)`, y su ausencia es deliberada.
// Ese es exactamente el gesto que hoy borra datos: `VehicleDocuments.js`
// reconstruye las cuatro claves en cada guardado, así que registrar el seguro
// escribe además `marbete: null` y BORRA el marbete. Mientras aquí no exista una
// función que acepte los cuatro documentos a la vez, nadie puede repetirlo.
//
// La clase va en la RUTA (`/documents/:kind`) y el cuerpo lleva UNA fecha.
// ────────────────────────────────────────────────────────────────────────────

/** Las cuatro clases, tal como las nombra la pantalla y las acepta el API. */
export const DOCUMENT_KINDS = ['marbete', 'seguro', 'licencia', 'inspeccion'];

/**
 * 🔴 LA FORMA VIEJA DEL MARBETE, que si no se lee cuesta un dato.
 *
 * En Firestore conviven DOS formas para el mismo documento: `{expiresAt}` (la
 * normal) y —solo para el marbete— **la fecha directa**, sin objeto que la
 * envuelva (db/MAPPING.md; `maintenanceRules.js:573` ya la contempla al leer).
 *
 * Quien lea solo `doc.expiresAt` sobre la forma vieja obtiene `undefined`, o sea
 * la pantalla enseña el marbete VACÍO... y al guardar escribe ese vacío encima.
 * No es un caso hipotético: es la ruta exacta por la que hoy se pierde el
 * marbete de un carro que sí lo tenía.
 *
 * Devuelve siempre `YYYY-MM-DD` o `''`, que es lo que come un `<input type=date>`.
 */
export function docDate(entry) {
  if (entry === null || entry === undefined || entry === '') return '';
  // La forma normal: `{ expiresAt }`.
  const bruto = typeof entry === 'object' && !(entry instanceof Date) && !entry.toDate
    ? entry.expiresAt
    : entry;                              // la forma vieja: la fecha, pelada.
  if (bruto === null || bruto === undefined || bruto === '') return '';
  // Un sello de Firestore o un Date; si no, un texto que ya suele venir en
  // `YYYY-MM-DD` y se recorta sin pasar por `new Date` para no restarle un día
  // por zona horaria.
  if (typeof bruto === 'string') return bruto.slice(0, 10);
  const d = bruto && typeof bruto.toDate === 'function' ? bruto.toDate() : bruto;
  if (!(d instanceof Date) || !Number.isFinite(d.getTime())) return '';
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/**
 * De la lista del API al objeto que la pantalla lee. Es el mismo puente que
 * `adaptDocuments` hace al leer la ficha, aplicado al acuse de la escritura para
 * que la pantalla se repinte con lo que el servidor tiene AHORA y no con lo que
 * el navegador cree haber mandado.
 */
export function adaptDocumentsAck(payload) {
  return adaptDocuments(payload && payload.documents);
}

/** Registrar (o corregir) UN documento. `expiresOn` va como `YYYY-MM-DD`. */
export async function setVehicleDocument(vehicleId, kind, expiresOn, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const payload = await trasEscribir(call(
    `/vehicles/${encodeURIComponent(vehicleId)}/documents/${encodeURIComponent(kind)}`,
    { method: 'PUT', body: { expiresOn }, signal },
  ));
  return adaptDocumentsAck(payload);
}

/**
 * Quitar UN documento. Borrar la fecha es borrar el documento: el API no tiene
 * un estado intermedio «registrado, pero sin fecha» — y esta pantalla tampoco
 * sabría pintarlo.
 */
export async function deleteVehicleDocument(vehicleId, kind, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const payload = await trasEscribir(call(
    `/vehicles/${encodeURIComponent(vehicleId)}/documents/${encodeURIComponent(kind)}`,
    { method: 'DELETE', signal },
  ));
  return adaptDocumentsAck(payload);
}

// ────────────────────────────────────────────────────────────────────────────
// LOS DIAGNÓSTICOS — se añade el turno, no se reenvía la conversación
//
// 🔴 AQUÍ TAMPOCO EXISTE `saveDiagnosis(hiloCompleto)`. La escritura de hoy
// (`Diagnose.js:157`) hace `addDoc` con el hilo ENTERO tras cada respuesta: N
// turnos dejan N documentos con 2,4,6…2N mensajes, del orden de N²+N mensajes
// guardados, y la lista de "los últimos 3 diagnósticos" acaba enseñando 3 veces
// la misma conversación. Con estas dos funciones el coste de guardar un turno es
// constante, porque no hay forma de mandar más que el turno.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Deja los mensajes en lo único que el API acepta: papel y texto.
 *
 * ⚠️ `attachments` y `timestamp` viajan hoy dentro de cada mensaje del chat y
 * NO tienen columna. El API los rechaza con un 400 que los nombra (es
 * `.strict()`), así que se quitan AQUÍ, en el borde, y a la vista. Descartarlos
 * en silencio dentro del servidor habría dejado al usuario creyendo que la foto
 * que adjuntó quedó guardada.
 */
export function diagnosisMessagesBody(messages) {
  return lista(messages)
    .map(m => ({
      role: m && m.role === 'user' ? 'user' : 'assistant',
      content: typeof m?.content === 'string' ? m.content : '',
    }))
    .filter(m => m.content !== '');
}

/**
 * Abrir la conversación con su PRIMER turno. Devuelve `{ id, messageCount }`:
 * el `id` es lo que hace falta para seguir añadiendo, así que quien llame tiene
 * que guardarlo.
 */
export async function createDiagnosis(vehicleId, { mode, messages } = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const body = { messages: diagnosisMessagesBody(messages) };
  // El modo es opcional en el API; mandar `undefined` sería un campo de más.
  if (mode) body.mode = String(mode).toLowerCase();
  return call(`/vehicles/${encodeURIComponent(vehicleId)}/diagnoses`, { method: 'POST', body, signal });
}

/** El turno siguiente, y nada más. */
export async function appendDiagnosisMessages(diagnosisId, messages, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/diagnoses/${encodeURIComponent(diagnosisId)}/messages`, {
    method: 'POST',
    body: { messages: diagnosisMessagesBody(messages) },
    signal,
  });
}

// ════════════════════════════════════════════════════════════════════════════
// EL LIBRO DE TRABAJO — los mismos servicios, mirados desde quien los hizo
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ 🔴 CENTAVOS ARRIBA, PESOS ABAJO. AQUÍ ES DONDE SE CRUZA LA FRONTERA      ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// El API publica `costCents` y las pantallas de dinero del taller suman
// `record.cost`, que está en PESOS porque así lo guarda Firestore. Si esto
// devolviera los centavos con el nombre `cost`, los ingresos del taller saldrían
// multiplicados por cien y nadie vería un error: vería el mejor mes de su vida.
//
// La división vive en `pesos()`, la MISMA función que ya usa `adaptRecord`, para
// que no haya dos traducciones del dinero de este dominio. Y un coste ausente
// sigue siendo `null` y no cero: cero afirma «fue gratis».
//
// ⚠️ El taller que NO tiene registrado el coste de un servicio suma `null`, y
// `(sum) + (null || 0)` es lo que hacen las pantallas — igual que hoy con
// Firestore, donde el campo simplemente no está.
// ════════════════════════════════════════════════════════════════════════════

/**
 * Tope de páginas al recorrer el libro entero.
 *
 * Mismo criterio —y mismo número— que `WORKSHOP_APPTS_MAX_PAGES`: un taller con
 * mucho movimiento tendría que esperar decenas de viajes antes de ver una cifra,
 * y una cifra tarde es peor que una cifra con una nota. Se corta Y SE DICE:
 * `truncated` sube hasta quien llama.
 *
 * 6 × 50 = 300 servicios por periodo. El corte por fecha lo hace el servidor, así
 * que esto son 300 servicios DEL PERIODO consultado, no de toda la historia.
 */
export const SERVICE_RECORDS_MAX_PAGES = 6;

/**
 * Una línea del libro, con los nombres que ya usan las pantallas de dinero:
 * `cost` en pesos y `serviceDate` como sello de tiempo (media app llama a
 * `.toDate()`, ver la traición nº 1 del encabezado).
 */
export function adaptServiceEntry(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    id: row.id,
    vehicleId: row.vehicleId ?? null,
    mechanicId: row.mechanicId ?? null,
    serviceTypeCode: row.serviceTypeCode ?? null,
    serviceType: row.serviceType ?? '',
    // 🔴 Centavos → pesos. Ver el recuadro.
    cost: pesos(row.costCents),
    serviceDate: ts(row.serviceDate),
    appointmentId: row.appointmentId ?? null,
    createdAt: ts(row.createdAt),
  };
}

export function adaptServiceEntryPage(payload, opts = {}) {
  return adaptPage(payload, 'items', adaptServiceEntry, {
    page: opts.page || 1,
    pageSize: opts.pageSize || RECORDS_PAGE_SIZE,
    que: 'servicios',
  });
}

/**
 * Una página del libro de trabajo.
 *
 * ⚠️ `workshopId` NO se manda: el API lo saca de la sesión, y mandarlo solo
 * abriría la duda de si se puede cambiar. `scope` sí, y siempre explícito: no
 * hay alcance por defecto que adivine si se pregunta por el negocio o por uno
 * mismo.
 */
export async function fetchServiceRecordsPage(filtros = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const query = pageQuery(filtros, RECORDS_PAGE_SIZE);
  query.scope = filtros.scope === 'mechanic' ? 'mechanic' : 'workshop';
  // Las fechas viajan en ISO. `from` acota el periodo EN EL SERVIDOR: sin él,
  // «este mes» obligaría a recorrer el historial entero para descartarlo casi
  // todo.
  if (filtros.from) query.from = new Date(filtros.from).toISOString();
  if (filtros.to) query.to = new Date(filtros.to).toISOString();
  const payload = await call('/service-records', { query, signal });
  return adaptServiceEntryPage(payload, query);
}

/**
 * TODO el trabajo registrado (hasta el tope declarado), recorriendo páginas.
 *
 * Las pantallas de ingresos y desempeño SUMAN: una página suelta no les sirve,
 * porque un total a medias no se lee como «faltan datos», se lee como un mes
 * malo.
 */
export async function fetchAllServiceRecords(filtros = {}, { apiFetchImpl, signal } = {}) {
  const pedir = (page) => fetchServiceRecordsPage(
    { ...filtros, page, pageSize: RECORDS_PAGE_SIZE },
    { apiFetchImpl, signal },
  );

  // 🔴 EN PARALELO, PERO NI UNA PÁGINA MÁS. El techo se aplica ANTES de pedir
  // (`Math.min`), así que se piden las mismas `min(totalPages, MAX)` de siempre:
  // cambia el cuándo, no el cuántas. Seis viajes de ~0,4 s en fila india son dos
  // segundos y medio de pantalla de ingresos en blanco.
  const primera = await pedir(1);
  const declaradas = primera.totalPages ?? 1;
  const paginas = Math.min(declaradas, SERVICE_RECORDS_MAX_PAGES);

  const resto = await Promise.allSettled(
    // `allSettled` y NO `all`: `all` rechazaría con lo que falle antes EN EL
    // TIEMPO (no deja rechazos sueltos: se suscribe a todas). Aquí sube siempre
    // la razón de la página más temprana, y NUNCA una suma a medias — que en
    // una pantalla de dinero no se lee como «faltan datos», se lee como un mes
    // malo.
    Array.from({ length: Math.max(0, paginas - 1) }, (_, i) => pedir(i + 2)),
  );
  const fallo = resto.find((r) => r.status === 'rejected');
  if (fallo) throw fallo.reason;

  // En orden de PÁGINA, no de llegada.
  const leidas = [primera, ...resto.map((r) => r.value)];
  const acumulados = leidas.flatMap((r) => r.items);
  const total = leidas[leidas.length - 1].total;

  return {
    records: acumulados,
    total: total === null ? acumulados.length : total,
    truncated: paginas < declaradas,
  };
}

// ════════════════════════════════════════════════════════════════════════════
// LA FLOTA — «mis carros y el historial de todos», que es lo que pinta la salud
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ 🔴 SIN HISTORIAL, `getVehicleAlerts` DEVUELVE UNA LISTA VACÍA — y una     ║
// ║    lista vacía se pinta como «TODO AL DÍA» sobre un carro que puede       ║
// ║    llevar el aceite vencido tres meses. El tacómetro no da error: da 100. ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// Cinco pantallas necesitan exactamente lo mismo (el home, el plan de salud, el
// menú inferior, el garaje y el contexto del asistente) y con Firestore lo
// resolvían con un `where('vehicleId','in',...)` por tandas de diez.
//
// 🔴 ESTO PEDÍA UN VIAJE POR CARRO, Y ERA UN VIAJE DE MÁS POR CARRO. Con tres
// carros eran tres round-trips de ~290 ms —cada uno pagando además su propio
// `verifyIdToken` y su consulta de rol— donde cabía UNO. Iban en `Promise.all`,
// así que no era una cascada; era peaje repetido. Ahora hay una ruta que acepta
// varios vehículos (`GET /fleet-records`), con la autorización resuelta id por
// id en el servidor: un carro ajeno mezclado con los propios no se cuela, se
// omite. Ver el recuadro sobre `listFleetRecords` en `api/src/services`.
//
// Vive AQUÍ y no en cada pantalla porque escribir cinco veces el mismo bucle es
// la deuda que esta migración está pagando, y porque el `vehicleId` que la app
// usa para agrupar hay que reponerlo (una fila del historial del API lo trae,
// pero un `null` en esa columna rompería el agrupado sin decir nada).
//
// ⚠️ LA FORMA QUE SALE DE AQUÍ NO CAMBIA: una lista PLANA de servicios, en el
// orden de la flota que se pidió y por fecha descendente dentro de cada carro,
// exactamente como la devolvía el bucle. Las cinco pantallas no se enteran.

/**
 * Cuántos carros caben en una petición. Es el tope del servidor
 * (`FLEET_MAX_VEHICLES` en `api/src/services/vehicles.ts`), repetido aquí para
 * TROCEAR antes de preguntar: una flota más grande que el tope no puede acabar
 * en un 400 que apague el tacómetro entero.
 */
export const FLEET_MAX_VEHICLES = 50;

function trozos(lista, tam) {
  const out = [];
  for (let i = 0; i < lista.length; i += tam) out.push(lista.slice(i, i + tam));
  return out;
}

/**
 * Los grupos del API → la lista plana de siempre.
 *
 * El carro que no venga en la respuesta (ajeno, borrado, o sin permiso vigente)
 * se trata como «sin servicios», que es lo mismo que hacía el `null` de
 * `fetchRecords`: la ausencia no es un fallo, es una denegación.
 */
export function adaptFleetRecords(payload, ids) {
  const grupos = payload && payload.vehicles;
  if (!Array.isArray(grupos)) {
    throw new ApiError('El API no devolvió los historiales de la flota.', { code: 'bad_response' });
  }
  const porCarro = new Map();
  for (const g of grupos) {
    if (!g || typeof g !== 'object' || !g.vehicleId) continue;
    const filas = Array.isArray(g.records) ? g.records : [];
    porCarro.set(
      g.vehicleId,
      filas.map(adaptRecord).filter(Boolean).map(r => ({ ...r, vehicleId: r.vehicleId || g.vehicleId })),
    );
  }
  // En el orden de la flota que se pidió, no en el que conteste el servidor.
  return ids.flatMap(id => porCarro.get(id) || []);
}

/**
 * El camino de antes, carro por carro. Sigue vivo A PROPÓSITO: durante un rato
 * el hosting nuevo convive con el API viejo (el API lo despliega una persona,
 * después de revisar), y ahí `/fleet-records` todavía no existe. Sin esta vuelta
 * atrás, la pantalla de salud de todos los clientes se quedaría sin historial —
 * o sea, en «TODO AL DÍA» sobre carros con el aceite vencido.
 */
async function fleetRecordsCarroPorCarro(lista, { apiFetchImpl, signal } = {}) {
  const porCarro = await Promise.all(lista.map(async (v) => {
    const p = await fetchRecords(v.id, { page: 1 }, { apiFetchImpl, signal });
    return (p ? p.records : []).map(r => ({ ...r, vehicleId: r.vehicleId || v.id }));
  }));
  return porCarro.flat();
}

export async function fetchFleetRecords(vehiculos, { apiFetchImpl, signal } = {}) {
  const lista = Array.isArray(vehiculos) ? vehiculos.filter(v => v && v.id) : [];
  const call = apiFetchImpl || apiFetch;

  const pedir = async () => {
    if (lista.length === 0) return [];
    const ids = lista.map(v => v.id);
    try {
      const tandas = await Promise.all(trozos(ids, FLEET_MAX_VEHICLES).map(async (tanda) => {
        const payload = await call('/fleet-records', {
          query: { vehicleIds: tanda.join(','), perVehicle: RECORDS_PAGE_SIZE },
          signal,
        });
        return adaptFleetRecords(payload, tanda);
      }));
      return tandas.flat();
    } catch (err) {
      // ⚠️ 404 AQUÍ SIGNIFICA «ESA RUTA NO EXISTE», no «no te corresponde»: la
      // ruta nueva nunca contesta 404 —el carro que no te toca se omite del
      // grupo— así que un 404 solo puede venir del embudo final de un API
      // anterior a este cambio. Cualquier otro fallo SUBE: quien llama tiene que
      // poder distinguir «al día» de «no pude preguntar».
      if (!esNoEncontrado(err)) throw err;
      return fleetRecordsCarroPorCarro(lista, { apiFetchImpl, signal });
    }
  };

  // Se memoriza la FLOTA entera, no cada carro: la clave son los identificadores
  // ordenados, así que dos pantallas con el mismo garaje comparten el viaje y un
  // garaje distinto (o el del admin mirando otra cuenta) no se cruza con este.
  if (apiFetchImpl || signal) return pedir();
  return conMemoria(`fleet-records|${lista.map(v => v.id).sort().join(',')}`, pedir);
}
